import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { hasOpenOverlay, hasTransientPopupOpen } from "@/hooks/useOverlayDismiss";
import { isEditableKeyboardTarget, shouldHandleAppChord } from "@/lib/keyboardShortcuts";
import {
  activateRovingItemByTestId,
  clearRovingItemFocus,
  getRovingItemElements,
  getVisibleRovingItemElements,
  navigateRovingItem,
  setRovingItemActive,
  type RovingChordItem
} from "@/lib/rovingChordList";

export type GoNavChordItem = RovingChordItem & {
  label: string;
};

const GO_NAV_ITEM_SELECTOR = "[data-go-nav-item]";
const GO_NAV_ROOT_SELECTOR = "[data-go-nav-chord-root]";

export default function useGoNavChordMode({
  items,
  enabled = true,
  rootRef,
  onCompactMenuOpen,
  onCompactMenuClose
}: {
  items: GoNavChordItem[];
  enabled?: boolean;
  rootRef?: RefObject<HTMLElement | null>;
  /** Compact header: open the primary-nav menu with key-cap chord hints instead of inline tabs. */
  onCompactMenuOpen?: () => void;
  /** Compact header: close the primary-nav menu when go-nav mode exits via Escape. */
  onCompactMenuClose?: () => void;
}) {
  const [goNavActive, setGoNavActive] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const getGoNavRoot = useCallback(() => rootRef?.current ?? document.querySelector(GO_NAV_ROOT_SELECTOR), [rootRef]);

  const deactivate = useCallback(() => {
    setGoNavActive(false);
    document.body.dataset.goNavMode = "false";

    const root = getGoNavRoot();
    if (root) {
      const rovingItems = getRovingItemElements(root, GO_NAV_ITEM_SELECTOR);
      clearRovingItemFocus(rovingItems);

      const focused = document.activeElement;
      if (focused instanceof HTMLElement && focused.matches(GO_NAV_ITEM_SELECTOR)) {
        focused.blur();
      }
    }
  }, [getGoNavRoot]);

  const focusInitialRovingItem = useCallback(() => {
    const root = getGoNavRoot();
    if (!root) return false;

    const rovingItems = getVisibleRovingItemElements(root, GO_NAV_ITEM_SELECTOR);
    if (rovingItems.length === 0) return false;

    const routeActiveItem = rovingItems.find((item) => item.getAttribute("data-route-active") === "true");
    setRovingItemActive(routeActiveItem ?? rovingItems[0], rovingItems);
    return true;
  }, [getGoNavRoot]);

  const activateDesktop = useCallback(() => {
    if (!enabled || itemsRef.current.length === 0) return;
    if (hasOpenOverlay() || hasTransientPopupOpen()) return;

    setGoNavActive(true);
    document.body.dataset.goNavMode = "true";

    const active = document.activeElement;
    if (isEditableKeyboardTarget(active)) {
      (active as HTMLElement).blur();
    }

    requestAnimationFrame(() => {
      focusInitialRovingItem();
    });
  }, [enabled, focusInitialRovingItem]);

  const activate = useCallback(() => {
    if (!enabled || itemsRef.current.length === 0) return;
    if (hasOpenOverlay() || hasTransientPopupOpen()) return;

    const active = document.activeElement;
    if (isEditableKeyboardTarget(active)) {
      (active as HTMLElement).blur();
    }

    const root = getGoNavRoot();
    const inlineRovingItems = root ? getVisibleRovingItemElements(root, GO_NAV_ITEM_SELECTOR) : [];

    if (inlineRovingItems.length > 0) {
      activateDesktop();
      return;
    }

    if (onCompactMenuOpen) {
      setGoNavActive(true);
      document.body.dataset.goNavMode = "true";
      onCompactMenuOpen();
      return;
    }

    activateDesktop();
  }, [activateDesktop, enabled, getGoNavRoot, onCompactMenuOpen]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (goNavActive) {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onCompactMenuClose?.();
          deactivate();
          return;
        }

        if (event.repeat) return;

        if (!event.metaKey && !event.ctrlKey && !event.altKey) {
          const key = event.key.toLowerCase();
          for (const item of itemsRef.current) {
            if (!item.chordKey || item.chordKey.toLowerCase() !== key) continue;
            if (!item.onActivate) continue;

            event.preventDefault();
            event.stopPropagation();
            item.onActivate();
            deactivate();
            return;
          }
        }

        const root = getGoNavRoot();
        if (!root) return;

        const rovingItems = getVisibleRovingItemElements(root, GO_NAV_ITEM_SELECTOR);
        if (rovingItems.length === 0) return;

        if (event.key === "Tab") {
          event.preventDefault();
          event.stopPropagation();
          navigateRovingItem(rovingItems, event.shiftKey ? "previous" : "next");
          return;
        }

        if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
          event.preventDefault();
          event.stopPropagation();
          navigateRovingItem(rovingItems, event.key === "ArrowRight" ? "next" : "previous");
          return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        if (event.key === "Enter" || event.key === " ") {
          const activeElement = document.activeElement;
          const dataTestId = activeElement instanceof HTMLElement ? activeElement.getAttribute("data-testid") : null;
          if (dataTestId && activateRovingItemByTestId(itemsRef.current, dataTestId, deactivate)) {
            event.preventDefault();
            event.stopPropagation();
          }
          return;
        }

        return;
      }

      if (!shouldHandleAppChord(event)) return;
      if (event.key.toLowerCase() !== "g") return;
      if (hasOpenOverlay() || hasTransientPopupOpen()) return;

      event.preventDefault();
      activate();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [activate, deactivate, enabled, getGoNavRoot, goNavActive, onCompactMenuClose]);

  useEffect(() => {
    if (!goNavActive) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      deactivate();
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [deactivate, goNavActive]);

  useEffect(() => {
    if (!goNavActive) return;

    const frame = requestAnimationFrame(() => {
      focusInitialRovingItem();
    });

    return () => cancelAnimationFrame(frame);
  }, [focusInitialRovingItem, goNavActive]);

  useEffect(() => {
    return () => {
      document.body.dataset.goNavMode = "false";
    };
  }, []);

  return { goNavActive, deactivateGoNav: deactivate };
}
